import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, formatMoney } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, FileDown, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { generateBudgetPdf } from "@/lib/budgetPdf";
import { useBranding } from "@/context/BrandingContext";
import { useAuth } from "@/context/AuthContext";

function BudgetForm({ open, onClose, patientId, planItems, onSaved }) {
  const [items, setItems] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [tax, setTax] = useState(0);
  const [validUntil, setValidUntil] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) return;
    // Pre-load items from plan (only planeado / en_proceso)
    const fromPlan = (planItems || [])
      .filter((p) => p.status === "planeado" || p.status === "en_proceso")
      .map((p) => ({
        description: p.name,
        tooth: p.tooth || "",
        qty: 1,
        unit_price: p.price || 0,
      }));
    setItems(fromPlan.length ? fromPlan : [{ description: "", tooth: "", qty: 1, unit_price: 0 }]);
    setDiscount(0); setTax(0); setValidUntil(""); setNotes("");
  }, [open, planItems]);

  const updateItem = (idx, field, value) => {
    setItems((arr) => arr.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));
  const addEmpty = () => setItems((arr) => [...arr, { description: "", tooth: "", qty: 1, unit_price: 0 }]);

  const subtotal = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0);
  const afterDiscount = Math.max(0, subtotal - (Number(discount) || 0));
  const taxAmount = afterDiscount * ((Number(tax) || 0) / 100);
  const total = afterDiscount + taxAmount;

  const submit = async (e) => {
    e.preventDefault();
    const valid = items.filter((it) => it.description?.trim());
    if (valid.length === 0) { toast.error("Agrega al menos un item con descripción"); return; }
    try {
      const { data } = await api.post(`/patients/${patientId}/budgets`, {
        items: valid.map((it) => ({
          description: it.description.trim(),
          tooth: it.tooth || "",
          qty: Number(it.qty) || 1,
          unit_price: Number(it.unit_price) || 0,
        })),
        discount: Number(discount) || 0,
        tax: Number(tax) || 0,
        valid_until: validUntil || null,
        notes: notes || "",
      });
      toast.success(`Presupuesto ${data.number} creado`);
      onSaved && onSaved(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-display tracking-tight text-2xl">Nuevo presupuesto</SheetTitle>
        </SheetHeader>
        <form onSubmit={submit} className="space-y-4 mt-6 pb-10" data-testid="budget-form">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="label-eyebrow">Items del presupuesto</div>
              <button type="button" onClick={addEmpty} className="text-xs font-bold uppercase tracking-widest text-primary hover:underline inline-flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Añadir línea
              </button>
            </div>
            <div className="border border-border rounded-sm overflow-hidden">
              <div className="grid grid-cols-12 px-3 py-2 border-b border-border bg-muted/40">
                <div className="col-span-5 label-eyebrow">Descripción</div>
                <div className="col-span-1 label-eyebrow">Diente</div>
                <div className="col-span-1 label-eyebrow">Cant.</div>
                <div className="col-span-2 label-eyebrow">V. unit.</div>
                <div className="col-span-2 label-eyebrow text-right">Total</div>
                <div className="col-span-1"></div>
              </div>
              {items.length === 0 && (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">Sin items.</div>
              )}
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-1 px-3 py-2 border-b border-border last:border-b-0 items-center" data-testid={`budget-row-${i}`}>
                  <input className="input-flat col-span-5 !h-9" placeholder="Procedimiento" value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)} />
                  <input className="input-flat font-mono col-span-1 !h-9" value={it.tooth} onChange={(e) => updateItem(i, "tooth", e.target.value)} />
                  <input type="number" className="input-flat font-mono col-span-1 !h-9" value={it.qty} onChange={(e) => updateItem(i, "qty", e.target.value)} />
                  <input type="number" step="1000" className="input-flat font-mono col-span-2 !h-9" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} />
                  <div className="col-span-2 text-right font-mono text-sm">{formatMoney((Number(it.qty) || 0) * (Number(it.unit_price) || 0))}</div>
                  <div className="col-span-1 flex justify-end">
                    <button type="button" onClick={() => removeItem(i)} className="p-1 text-muted-foreground hover:text-destructive">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow block mb-2">Descuento (COP)</label>
              <input type="number" step="1000" className="input-flat font-mono" value={discount} onChange={(e) => setDiscount(e.target.value)} />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">IVA (%)</label>
              <input type="number" step="1" className="input-flat font-mono" value={tax} onChange={(e) => setTax(e.target.value)} />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Vigencia hasta</label>
              <input type="date" className="input-flat font-mono" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Notas / términos</label>
            <textarea rows={3} className="input-flat h-auto py-2" placeholder="Forma de pago, garantía, observaciones…" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          <div className="card-flat p-4 space-y-1 font-mono text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{formatMoney(subtotal)}</span></div>
            {Number(discount) > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Descuento</span><span>- {formatMoney(discount)}</span></div>}
            {Number(tax) > 0 && <div className="flex justify-between"><span className="text-muted-foreground">IVA ({tax}%)</span><span>{formatMoney(taxAmount)}</span></div>}
            <div className="flex justify-between text-base font-bold pt-2 border-t border-border mt-2">
              <span>Total</span><span>{formatMoney(total)}</span>
            </div>
          </div>

          <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-background/95 border-t border-border backdrop-blur">
            <button type="submit" className="btn-primary w-full justify-center" data-testid="budget-save">
              <Plus className="w-4 h-4" /> Crear presupuesto
            </button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

export default function Budgets({ patient, planItems }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const { branding } = useBranding();
  const { user } = useAuth();

  const load = async () => {
    const { data } = await api.get(`/patients/${patient.id}/budgets`);
    setList(data);
  };
  useEffect(() => { load(); }, [patient?.id]); // eslint-disable-line
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {}); }, []);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar presupuesto?")) return;
    try { await api.delete(`/budgets/${id}`); load(); }
    catch { toast.error("No se pudo eliminar"); }
  };

  const signerFor = (b) => users.find((u) => u.id === b.created_by) || user || null;

  const exportPdf = async (b) => {
    try {
      generateBudgetPdf(b, patient, branding, signerFor(b));
    } catch (e) { toast.error("No se pudo generar el PDF"); }
  };

  return (
    <div className="space-y-4" data-testid="budgets-section">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-eyebrow">Presupuestos</div>
          <p className="text-sm text-muted-foreground mt-1">{list.length} presupuesto(s) generados</p>
        </div>
        <button onClick={() => setOpen(true)} className="btn-primary" data-testid="new-budget-btn">
          <Plus className="w-4 h-4" /> Nuevo presupuesto
        </button>
      </div>

      <div className="card-flat overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 border-b border-border bg-muted/40">
          <div className="col-span-2 label-eyebrow">Número</div>
          <div className="col-span-3 label-eyebrow">Fecha</div>
          <div className="col-span-2 label-eyebrow">Items</div>
          <div className="col-span-3 label-eyebrow text-right">Total</div>
          <div className="col-span-2 label-eyebrow text-right">Acciones</div>
        </div>
        {list.length === 0 && (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">Sin presupuestos. Crea el primero.</div>
        )}
        {list.map((b) => (
          <div key={b.id} className="grid grid-cols-12 px-4 py-3 border-b border-border last:border-b-0 items-center hover:bg-muted/40" data-testid={`budget-row-list-${b.id}`}>
            <div className="col-span-2 font-mono text-sm font-bold text-primary">{b.number}</div>
            <div className="col-span-3 font-mono text-xs text-muted-foreground">{new Date(b.created_at).toLocaleString("es-CO")}</div>
            <div className="col-span-2 text-sm">{b.items?.length || 0} items</div>
            <div className="col-span-3 font-mono font-bold text-right">{formatMoney(b.total)}</div>
            <div className="col-span-2 flex justify-end gap-1">
              <button onClick={() => exportPdf(b)} className="px-3 h-9 rounded-sm border border-border hover:bg-primary hover:text-primary-foreground transition inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest" data-testid={`budget-pdf-${b.id}`}>
                <FileDown className="w-3.5 h-3.5" /> PDF
              </button>
              <button onClick={() => remove(b.id)} className="p-2 text-muted-foreground hover:text-destructive">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <BudgetForm
        open={open}
        onClose={() => setOpen(false)}
        patientId={patient.id}
        planItems={planItems}
        onSaved={(b) => { setOpen(false); load(); generateBudgetPdf(b, patient, branding, signerFor(b)); }}
      />
    </div>
  );
}
